import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useToast } from '../../contexts/ToastContext';
import TelerikDatabaseGrid from '../../components/grids/TelerikDatabaseGrid';
import { getDatabaseTables, getTableData } from '../../data/databaseExplorerData';
import { dropDown } from '../../data/interfaces';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { FaTable, FaFileExcel } from 'react-icons/fa';
import '../Styles.css';

const DatabaseExplorer = () => {
  const [tables, setTables] = useState<dropDown[]>([]);
  const [selectedTable, setSelectedTable] = useState<dropDown | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchTables = async () => {
      try {
        const tablesData = await getDatabaseTables();
        setTables(tablesData);
        setInitialLoading(false);
      } catch (error) {
        console.error('Error fetching tables:', error);
        showToast('Failed to load database tables', 'error');
        setInitialLoading(false);
      }
    };

    fetchTables();
  }, []);

  const handleTableSelect = async (e: any) => {
    const selected = e.target.value;
    setSelectedTable(selected);
    
    if (selected && selected.value) {
      setLoading(true);
      try {
        const data = await getTableData(selected.text);
        setTableData(data);
        
        // Extract column names from the first row
        if (data.length > 0) {
          setColumns(Object.keys(data[0]));
        } else {
          setColumns([]);
        }
        
        showToast(`Loaded ${data.length} rows from ${selected.text}`, 'success');
      } catch (error) {
        console.error('Error fetching table data:', error);
        showToast('Failed to load table data', 'error');
        setTableData([]);
        setColumns([]);
      } finally {
        setLoading(false);
      }
    }
  };

  // Export to Excel function
  const exportToExcel = () => {
    if (!tableData.length || !selectedTable) {
      showToast('No data to export', 'info');
      return;
    }

    try {
      // Validate that we have proper tabular data
      if (!Array.isArray(tableData) || tableData.length === 0) {
        showToast('No valid data to export', 'info');
        return;
      }

      // Check if the data has consistent structure (all rows have similar properties)
      const firstRowKeys = Object.keys(tableData[0]);
      const hasConsistentStructure = tableData.every(row => 
        typeof row === 'object' && row !== null && Object.keys(row).length > 0
      );

      if (!hasConsistentStructure) {
        showToast('Data structure is not suitable for export', 'error');
        return;
      }

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(tableData, { header: columns });

      // Add worksheet to workbook
      const sheetName = selectedTable.text.length > 31 
        ? selectedTable.text.substring(0, 31) // Excel sheet name limit
        : selectedTable.text;
      
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Generate filename with timestamp
      const dateStr = new Date().toISOString().split('T')[0];
      const timeStr = new Date().toISOString().split('T')[1].split('.')[0].replace(/:/g, '-');
      const filename = `${selectedTable.text}_${dateStr}_${timeStr}.xlsx`;
      
      // Write file
      XLSX.writeFile(wb, filename);
      
      showToast(`Successfully exported ${tableData.length} rows from ${selectedTable.text}`, 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showToast('Failed to export data. Please check if the data is in a valid format.', 'error');
    }
  };

  if (initialLoading) {
    return (
      <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading database tables...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '1rem',
        gap: '1rem'
      }}>
        <FaTable style={{ fontSize: '1.5rem', color: '#21433b' }} />
        <h1 style={{ margin: 0, color: '#21433b' }}>Database Explorer</h1>
      </div>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '1rem', 
        marginBottom: '1rem',
        backgroundColor: '#f5f5f5',
        padding: '1rem',
        borderRadius: '0.5rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ fontWeight: 'bold', color: '#21433b' }}>Select Table/Procedure:</label>
          <DropDownList
            data={tables}
            textField="text"
            dataItemKey="value"
            value={selectedTable}
            onChange={handleTableSelect}
            defaultItem={{ text: "Choose a dataset...", value: null }}
            style={{ minWidth: '250px' }}
          />
        </div>
      </div>

      {selectedTable && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1rem' 
        }}>
          <div>
            <h3 style={{ color: '#21433b', margin: '0 0 0.5rem 0' }}>
              {selectedTable.text}
            </h3>
            <p style={{ margin: 0, color: '#666' }}>
              {loading ? 'Loading...' : `${tableData.length} rows, ${columns.length} columns`}
            </p>
          </div>
          {!loading && tableData.length > 0 && (
            <button
              onClick={exportToExcel}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#1d6f42',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#155a35';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#1d6f42';
              }}
            >
              <FaFileExcel style={{ fontSize: '1rem' }} />
              Export to Excel
            </button>
          )}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading table data...</p>
        </div>
      )}

      {!loading && selectedTable && tableData.length > 0 && (
        <TelerikDatabaseGrid
          key={selectedTable.text}
          data={tableData}
          columns={columns}
          tableName={selectedTable.text}
          onExport={() => showToast(`Successfully exported data from ${selectedTable.text}`, 'success')}
        />
      )}

      {!loading && selectedTable && tableData.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem',
          backgroundColor: '#f9f9f9',
          borderRadius: '0.5rem',
          border: '1px solid #ddd'
        }}>
          <p style={{ margin: 0, color: '#666' }}>No data found in this table.</p>
        </div>
      )}
    </div>
  );
};

export default DatabaseExplorer;
